import type { MonitorArrival } from '@/apis/monitor';
import { getUserTimeZone } from '@/common/interceptor/interceptor';
import { formatDurationMinutes as formatMinutes } from '../Home/map/needsAttention';

/** Rows that fit the TV board under the map without crowding it. */
export const BOARD_ROWS = 6;

export type ArrivalTone = 'ok' | 'soon' | 'overdue' | 'done';

/**
 * The API returns rows in landing order, so recent deliveries come first.
 * Keep only the latest delivery as context and fill the rest with what is
 * still to land.
 */
export const pickBoardRows = (
  arrivals: MonitorArrival[],
  max: number = BOARD_ROWS
): MonitorArrival[] => {
  const delivered = arrivals.filter((a) => a.status === 'delivered');
  const upcoming = arrivals.filter((a) => a.status !== 'delivered');
  return [...delivered.slice(-1), ...upcoming].slice(0, max);
};

/**
 * The profile timezone the rest of the app shows dates in, or undefined (the
 * browser's own zone) when the profile holds something Intl rejects, so a
 * bad value never takes the TV down with a RangeError.
 */
export const displayTimeZone = (): string | undefined => {
  const zone = getUserTimeZone();
  try {
    new Intl.DateTimeFormat([], { timeZone: zone });
    return zone;
  } catch {
    return undefined;
  }
};

/** "20:00" today, "Tue 20:00" on any other day, so tomorrow never reads as today. */
export const formatBoardTime = (
  iso: string | null,
  now: Date = new Date()
): string => {
  if (!iso) return '—';
  const timeZone = displayTimeZone();
  const at = new Date(iso);
  const time = at.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone,
  });
  const day = (date: Date) => date.toLocaleDateString('en-CA', { timeZone });
  if (day(at) === day(now)) return time;
  return `${at.toLocaleDateString([], { weekday: 'short', timeZone })} ${time}`;
};

/** Pill text and colour, matching the shipment card's "Arrives in" banner. */
export const describeArrivalStatus = (
  arrival: MonitorArrival
): { label: string; tone: ArrivalTone } => {
  const minutes = arrival.minutes_vs_schedule ?? 0;
  switch (arrival.status) {
    case 'delivered':
      return {
        label: `Delivered ${formatBoardTime(arrival.actual_delivery_date)}`,
        tone: 'done',
      };
    case 'late':
      return {
        label: `Late ${formatMinutes(minutes)}`,
        tone: minutes >= 60 ? 'overdue' : 'soon',
      };
    case 'early':
      return { label: `Early ${formatMinutes(-minutes)}`, tone: 'ok' };
    default:
      return { label: 'On time', tone: 'ok' };
  }
};

export const formatDestination = (arrival: MonitorArrival): string => {
  const { city, state, name } = arrival.destination;
  return [city, state].filter(Boolean).join(', ') || name || '—';
};

/** Zoomlogi ETA, or the schedule when there is no ETA yet. */
export const landingTime = (arrival: MonitorArrival): string | null =>
  arrival.actual_delivery_date ??
  arrival.estimated_delivery_date ??
  arrival.scheduled_delivery_date;
